'use client';

import { useEffect, useRef } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { useTranslation } from '@/hooks/useTranslation';
import { useRTL } from '@/hooks/useRTL';
import { Card, CardContent } from '@/components/ui/card';
import OptimizedImage from '@/components/optimized-media/OptimizedImage';
import { Stethoscope, Monitor, Heart, Building, Users, DollarSign, Award, Zap } from 'lucide-react';

gsap.registerPlugin(ScrollTrigger);

export default function WhyChooseSection() {
    const { t, isLoading } = useTranslation('root-canal');
    const { isRTL } = useRTL();
    const sectionRef = useRef<HTMLElement>(null);
    const titleRef = useRef<HTMLDivElement>(null);
    const cardsRef = useRef<HTMLDivElement>(null);
    const trustRef = useRef<HTMLDivElement>(null);
    const clinicRef = useRef<HTMLDivElement>(null);
    const techRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (!sectionRef.current) return;

        const ctx = gsap.context(() => {
            // Title animation
            if (titleRef.current) {
                gsap.fromTo(titleRef.current,
                    { opacity: 0, y: 50 },
                    {
                        opacity: 1,
                        y: 0,
                        duration: 1,
                        ease: "power2.out",
                        scrollTrigger: {
                            trigger: titleRef.current,
                            start: "top 85%",
                            toggleActions: "play none none reverse"
                        }
                    }
                );
            }

            // Reasons cards animation
            if (cardsRef.current?.children) {
                const cards = Array.from(cardsRef.current.children);
                gsap.fromTo(cards,
                    { opacity: 0, y: 30 },
                    {
                        opacity: 1,
                        y: 0,
                        duration: 0.6,
                        stagger: 0.15,
                        ease: "power2.out",
                        scrollTrigger: {
                            trigger: cardsRef.current,
                            start: "top 85%",
                            toggleActions: "play none none reverse"
                        }
                    }
                );
            }

            // Trust section animation
            if (trustRef.current) {
                gsap.fromTo(trustRef.current,
                    { opacity: 0, y: 30 },
                    {
                        opacity: 1,
                        y: 0,
                        duration: 0.6,
                        ease: "power2.out",
                        scrollTrigger: {
                            trigger: trustRef.current,
                            start: "top 85%",
                            toggleActions: "play none none reverse"
                        }
                    }
                );
            }

            // Clinic photos animation
            if (clinicRef.current?.children) {
                const photos = Array.from(clinicRef.current.children);
                gsap.fromTo(photos,
                    { opacity: 0, scale: 0.9 },
                    {
                        opacity: 1,
                        scale: 1,
                        duration: 0.6,
                        stagger: 0.1,
                        ease: "power2.out",
                        scrollTrigger: {
                            trigger: clinicRef.current,
                            start: "top 85%",
                            toggleActions: "play none none reverse"
                        }
                    }
                );
            }

            // Tech section animation
            if (techRef.current?.children) {
                const techItems = Array.from(techRef.current.children);
                gsap.fromTo(techItems,
                    { opacity: 0, scale: 0.8 },
                    {
                        opacity: 1,
                        scale: 1,
                        duration: 0.5,
                        stagger: 0.1,
                        ease: "back.out(1.7)",
                        scrollTrigger: {
                            trigger: techRef.current,
                            start: "top 85%",
                            toggleActions: "play none none reverse"
                        }
                    }
                );
            }

        }, sectionRef);

        return () => {
            ctx.revert();
        };
    }, []);

    const reasons = [
        {
            icon: Stethoscope,
            title: t('whyChoose.reasons.specialists.title'),
            description: t('whyChoose.reasons.specialists.description')
        },
        {
            icon: Monitor,
            title: t('whyChoose.reasons.technology.title'),
            description: t('whyChoose.reasons.technology.description')
        },
        {
            icon: Heart,
            title: t('whyChoose.reasons.painless.title'),
            description: t('whyChoose.reasons.painless.description')
        },
        {
            icon: Building,
            title: t('whyChoose.reasons.facilities.title'),
            description: t('whyChoose.reasons.facilities.description')
        },
        {
            icon: Users,
            title: t('whyChoose.reasons.multilingual.title'),
            description: t('whyChoose.reasons.multilingual.description')
        },
        {
            icon: DollarSign,
            title: t('whyChoose.reasons.pricing.title'),
            description: t('whyChoose.reasons.pricing.description')
        }
    ];

    return (
        <section id="why-choose" ref={sectionRef} className="py-16 lg:py-24 bg-white dark:bg-slate-800">
            <div className="container">
                <div ref={titleRef} className="text-center mb-16">
                    {isLoading ? (
                        <div className="animate-pulse">
                            <div className="h-12 bg-gray-200 dark:bg-gray-700 rounded mb-6 max-w-4xl mx-auto"></div>
                            <div className="h-6 bg-gray-200 dark:bg-gray-700 rounded max-w-3xl mx-auto"></div>
                        </div>
                    ) : (
                        <>
                            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-navy dark:text-white font-serif leading-tight mb-6">
                                {t('whyChoose.title.part1')} <span className="text-gold">{t('whyChoose.title.part2')}</span> {t('whyChoose.title.part3')}
                            </h2>
                            <p className="text-lg text-slate-600 dark:text-slate-300 max-w-3xl mx-auto">
                                {t('whyChoose.subtitle')}
                            </p>
                        </>
                    )}
                </div>

                <div ref={cardsRef} className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
                    {isLoading ? (
                        Array.from({ length: 6 }).map((_, index) => (
                            <Card key={index} className="h-full animate-pulse">
                                <CardContent className="p-8 text-center h-full flex flex-col">
                                    <div className="w-16 h-16 bg-gray-200 dark:bg-gray-700 rounded-2xl mx-auto mb-6"></div>
                                    <div className="h-6 bg-gray-200 dark:bg-gray-700 rounded mb-4"></div>
                                    <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded mb-2"></div>
                                    <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-3/4 mx-auto"></div>
                                </CardContent>
                            </Card>
                        ))
                    ) : (
                        reasons.map((reason, index) => (
                            <Card key={index} className="h-full border-slate-200 dark:border-slate-700 hover:shadow-xl transition-all duration-300 group hover:-translate-y-2 hover:border-gold/50 bg-gradient-to-br from-white to-slate-50 dark:from-slate-700 dark:to-slate-800">
                                <CardContent className="p-8 text-center h-full flex flex-col">
                                    <div className="w-16 h-16 bg-gold/20 rounded-2xl flex items-center justify-center mx-auto mb-6 group-hover:bg-gold/30 transition-all duration-300 group-hover:scale-110">
                                        <reason.icon className="w-8 h-8 text-gold" />
                                    </div>

                                    <h3 className="text-xl font-bold text-navy dark:text-white mb-4">
                                        {reason.title}
                                    </h3>

                                    <p className="text-slate-600 dark:text-slate-300 leading-relaxed flex-1 flex items-center text-center">
                                        {reason.description}
                                    </p>
                                </CardContent>
                            </Card>
                        ))
                    )}
                </div>

                {/* Additional Trust Elements */}
                <div ref={trustRef} className="max-w-4xl mx-auto mt-16">
                    <Card className="bg-gold/10 border-gold/30">
                        <CardContent className="p-8 text-center">
                            <div className={`flex items-center justify-center gap-3 mb-6 ${isRTL ? 'flex-row-reverse' : ''}`}>
                                <Award className="w-8 h-8 text-gold" />
                                <h3 className="text-2xl font-bold text-navy dark:text-white">
                                    {t('whyChoose.trust.title')}
                                </h3>
                            </div>

                            <div className="grid md:grid-cols-3 gap-8">
                                <div className={`${isRTL ? 'text-right' : 'text-center'}`}>
                                    <div className={`text-3xl font-bold text-gold mb-2 ${isRTL ? 'text-right' : 'text-center'}`}>{t('whyChoose.trust.stats.procedures.number')}</div>
                                    <div className={`text-sm text-slate-600 dark:text-slate-300 ${isRTL ? 'text-right' : 'text-center'}`}>
                                        {t('whyChoose.trust.stats.procedures.title')}<br />
                                        <span className="text-xs opacity-75">{t('whyChoose.trust.stats.procedures.subtitle')}</span>
                                    </div>
                                </div>

                                <div className={`${isRTL ? 'text-right' : 'text-center'}`}>
                                    <div className={`text-3xl font-bold text-gold mb-2 ${isRTL ? 'text-right' : 'text-center'}`}>{t('whyChoose.trust.stats.countries.number')}</div>
                                    <div className={`text-sm text-slate-600 dark:text-slate-300 ${isRTL ? 'text-right' : 'text-center'}`}>
                                        {t('whyChoose.trust.stats.countries.title')}<br />
                                        <span className="text-xs opacity-75">{t('whyChoose.trust.stats.countries.subtitle')}</span>
                                    </div>
                                </div>

                                <div className={`${isRTL ? 'text-right' : 'text-center'}`}>
                                    <div className={`text-3xl font-bold text-gold mb-2 ${isRTL ? 'text-right' : 'text-center'}`}>{t('whyChoose.trust.stats.satisfaction.number')}</div>
                                    <div className={`text-sm text-slate-600 dark:text-slate-300 ${isRTL ? 'text-right' : 'text-center'}`}>
                                        {t('whyChoose.trust.stats.satisfaction.title')}<br />
                                        <span className="text-xs opacity-75">{t('whyChoose.trust.stats.satisfaction.subtitle')}</span>
                                    </div>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                </div>

                {/* Clinic Photos */}
                <div ref={clinicRef} className="mt-16">
                    <h3 className="text-2xl font-bold text-navy dark:text-white mb-8 text-center">
                        {t('whyChoose.clinic.title')}
                    </h3>

                    <div className="grid md:grid-cols-3 gap-6">
                        {([
                            { key: 'treatment', src: '/images/treatments/root-canal/treatment-room.webp' },
                            { key: 'team', src: '/images/treatments/root-canal/expert-team.webp' },
                            { key: 'comfort', src: '/images/treatments/root-canal/comfortable-environment.webp' },
                        ] as const).map(({ key, src }) => (
                            <Card key={key} className="group overflow-hidden border-2 border-transparent hover:border-gold transition-all duration-300">
                                <div className="relative h-48 overflow-hidden">
                                    <OptimizedImage
                                        src={src}
                                        alt={t(`whyChoose.clinic.images.${key}.title`)}
                                        fill
                                        sizes="(max-width: 768px) 100vw, 33vw"
                                        className="transition-transform duration-500 group-hover:scale-105"
                                    />
                                    <div className="absolute inset-0 bg-gradient-to-t from-navy/80 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 z-10"></div>
                                    <div className="absolute bottom-4 left-4 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300 z-10">
                                        <h4 className="font-bold">{t(`whyChoose.clinic.images.${key}.overlay.title`)}</h4>
                                        <p className="text-sm">{t(`whyChoose.clinic.images.${key}.overlay.description`)}</p>
                                    </div>
                                </div>
                            </Card>
                        ))}
                    </div>
                </div>

                {/* Modern Technology Section */}
                <div ref={techRef} className="mt-16">
                    <Card className="bg-gradient-to-br from-navy to-navy-950 border-gold/30 overflow-hidden">
                        <CardContent className="p-8">
                            <div className="text-center mb-8">
                                <div className={`inline-flex items-center gap-2 bg-gold/20 rounded-full px-6 py-3 mb-4 ${isRTL ? 'flex-row-reverse' : ''}`}>
                                    <Zap className="w-6 h-6 text-gold" />
                                    <span className="text-white font-semibold">{t('whyChoose.modernTech.badge')}</span>
                                </div>
                                <h3 className="text-3xl font-bold text-white mb-4">
                                    {t('whyChoose.modernTech.title')}
                                </h3>
                                <p className={`text-slate-300 text-lg leading-relaxed max-w-3xl mx-auto ${isRTL ? 'text-right' : 'text-center'}`}>
                                    {t('whyChoose.modernTech.subtitle')}
                                </p>
                            </div>

                            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
                                {[
                                    {
                                        icon: "🔬",
                                        title: t('whyChoose.modernTech.technologies.microscopic.title'),
                                        description: t('whyChoose.modernTech.technologies.microscopic.description')
                                    },
                                    {
                                        icon: "⚡",
                                        title: t('whyChoose.modernTech.technologies.laser.title'),
                                        description: t('whyChoose.modernTech.technologies.laser.description')
                                    },
                                    {
                                        icon: "🖥️",
                                        title: t('whyChoose.modernTech.technologies.digital.title'),
                                        description: t('whyChoose.modernTech.technologies.digital.description')
                                    },
                                    {
                                        icon: "🎯",
                                        title: t('whyChoose.modernTech.technologies.rotary.title'),
                                        description: t('whyChoose.modernTech.technologies.rotary.description')
                                    }
                                ].map((tech, index) => (
                                    <div key={index} className="text-center p-4 bg-white/10 rounded-xl backdrop-blur-sm hover:bg-white/20 transition-all duration-300">
                                        <div className="text-4xl mb-3">{tech.icon}</div>
                                        <h4 className="font-bold text-white mb-2">{tech.title}</h4>
                                        <p className="text-slate-300 text-sm text-center">{tech.description}</p>
                                    </div>
                                ))}
                            </div>
                        </CardContent>
                    </Card>
                </div>
            </div>
        </section>
    );
} 